// Deterministic mock dataset for the Insights dashboard. The Hasura schema
// doesn't carry product groups, seasons, channels or weekday-of-week stats yet,
// so we synthesise a realistic distribution to fill the dashboard. Once the
// real columns land, swap the generator for a Hasura aggregate query — the
// shapes below are stable.

const lcg = (seed: number) => {
  let s = seed >>> 0;
  return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
};

const PRODUCT_NAMES = [
  'Marlene Sneaker', 'Lyra Sandalo', 'Tora Loafer', 'Nora Pump', 'Sofia Boot',
  'Elena Ballerina', 'Carla Mokassin', 'Bianca Stiefelette', 'Diana Riemchen',
  'Greta Espadrille', 'Helga Slipper', 'Inga Sneaker', 'Jana Pump',
  'Kira Stiefel', 'Lara Sandale', 'Mia Loafer', 'Nina Boot', 'Olga Pump',
  'Petra Mokassin', 'Quira Sandale',
];

const PRODUCT_GROUPS = ['Sneaker', 'Pumps', 'Stiefel', 'Sandalen', 'Loafer', 'Espadrilles'] as const;
const SEASONS = ['SS25', 'AW24', 'SS24', 'AW23'] as const;
const CHANNELS = ['E-Mail', 'PDP', 'MyAccount'] as const;
const STATUSES = ['pending', 'published', 'hidden', 'rejected'] as const;
const HIDDEN_REASONS = ['Beleidigung', 'PII / Datenschutz'] as const;
const COUNTRIES = ['DE', 'AT', 'CH', 'IT', 'FR', 'ES', 'PT', 'NL', 'BE', 'LU', 'IE', 'DK', 'SE', 'FI'] as const;

export type InsightsReview = {
  id: string;
  productId: string;
  rating: number;
  channel: (typeof CHANNELS)[number];
  status: (typeof STATUSES)[number];
  hiddenReason: (typeof HIDDEN_REASONS)[number] | null;
  weekday: number; // 0=Mon
  daysAgo: number; // for filtering
  country: (typeof COUNTRIES)[number];
  productGroup: (typeof PRODUCT_GROUPS)[number];
  season: (typeof SEASONS)[number];
  hasText: boolean;
  loyalty: boolean;
};

export type InsightsProduct = {
  id: string;
  name: string;
  sku: string;
  group: (typeof PRODUCT_GROUPS)[number];
  season: (typeof SEASONS)[number];
  reviewCount: number;
  lifetimeAvg: number;
  last7Avg: number;
  last30Avg: number;
  last60Avg: number;
  last90Avg: number;
  last7Count: number;
  last30Count: number;
  topPositiveTopics: readonly string[];
  topNegativeTopics: readonly string[];
};

export type InsightsData = {
  reviews: readonly InsightsReview[];
  products: readonly InsightsProduct[];
};

export function buildInsightsData(): InsightsData {
  const rng = lcg(42);
  const reviews: InsightsReview[] = [];
  const products: InsightsProduct[] = [];

  // 20 products
  for (let i = 0; i < PRODUCT_NAMES.length; i++) {
    const lifetime = 3.4 + rng() * 1.5;
    const last30 = lifetime + (rng() - 0.5) * 0.8;
    const last7 = last30 + (rng() - 0.5) * 0.6;
    const last60 = lifetime + (rng() - 0.5) * 0.5;
    const last90 = lifetime + (rng() - 0.5) * 0.4;
    products.push({
      id: `p${i}`,
      name: PRODUCT_NAMES[i] ?? `Produkt ${i}`,
      sku: `1-${(22000 + Math.floor(rng() * 999)).toFixed(0)}-${(10 + Math.floor(rng() * 89)).toFixed(0)}`,
      group: PRODUCT_GROUPS[Math.floor(rng() * PRODUCT_GROUPS.length)] ?? 'Sneaker',
      season: SEASONS[Math.floor(rng() * SEASONS.length)] ?? 'SS25',
      reviewCount: 80 + Math.floor(rng() * 220),
      lifetimeAvg: round1(lifetime),
      last7Avg: round1(Math.max(1, Math.min(5, last7))),
      last30Avg: round1(Math.max(1, Math.min(5, last30))),
      last60Avg: round1(Math.max(1, Math.min(5, last60))),
      last90Avg: round1(Math.max(1, Math.min(5, last90))),
      last7Count: 4 + Math.floor(rng() * 12),
      last30Count: 18 + Math.floor(rng() * 42),
      topPositiveTopics: pickTopics(rng, [
        'Passform top', 'Bequem', 'Hochwertig', 'Schnelle Lieferung',
        'Schöne Optik', 'Klassischer Schnitt', 'Gutes Material',
      ]),
      topNegativeTopics: pickTopics(rng, [
        'Größe fällt klein aus', 'Sohle löst sich', 'Drückt vorne',
        'Innensohle hart', 'Riemen zu eng', 'Farbe weicht ab',
      ]),
    });
  }

  // 4 800 reviews ≈ 240 per product across the year
  const total = 4800;
  for (let i = 0; i < total; i++) {
    const product = products[Math.floor(rng() * products.length)] ?? products[0]!;
    const status = pickStatus(rng);
    const rating = pickRating(rng, status);
    const hiddenReason = status === 'hidden' || status === 'rejected'
      ? HIDDEN_REASONS[rng() < 0.65 ? 0 : 1]!
      : null;
    reviews.push({
      id: `r${i}`,
      productId: product.id,
      rating,
      channel: CHANNELS[pickChannelIdx(rng)] ?? 'PDP',
      status,
      hiddenReason,
      weekday: Math.floor(rng() * 7),
      daysAgo: Math.floor(rng() * 365),
      country: COUNTRIES[Math.floor(rng() * COUNTRIES.length)] ?? 'DE',
      productGroup: product.group,
      season: product.season,
      hasText: rng() > 0.18,
      loyalty: rng() > 0.66,
    });
  }

  return { reviews, products };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function pickTopics(rng: () => number, pool: readonly string[]): readonly string[] {
  const out: string[] = [];
  const indices = new Set<number>();
  while (out.length < 3) {
    const i = Math.floor(rng() * pool.length);
    if (indices.has(i)) continue;
    indices.add(i);
    const t = pool[i];
    if (t) out.push(t);
  }
  return out;
}

function pickRating(rng: () => number, status: (typeof STATUSES)[number]): number {
  if (status === 'hidden' || status === 'rejected') {
    // Hidden reviews skew low
    return rng() < 0.55 ? 1 : 2;
  }
  // Published reviews follow the typical e-commerce J-curve
  const r = rng();
  if (r < 0.55) return 5;
  if (r < 0.78) return 4;
  if (r < 0.92) return 3;
  if (r < 0.98) return 2;
  return 1;
}

function pickStatus(rng: () => number): (typeof STATUSES)[number] {
  const r = rng();
  if (r < 0.04) return 'pending';
  if (r < 0.07) return 'rejected';
  if (r < 0.11) return 'hidden';
  return 'published';
}

function pickChannelIdx(rng: () => number): number {
  const r = rng();
  if (r < 0.62) return 0; // E-Mail
  if (r < 0.86) return 1; // PDP
  return 2; // MyAccount
}

export const PRODUCT_GROUPS_ALL = PRODUCT_GROUPS;
export const SEASONS_ALL = SEASONS;
export const CHANNELS_ALL = CHANNELS;
export const STATUSES_ALL = STATUSES;
export const COUNTRIES_ALL = COUNTRIES;
